/**
 * SelectContext — Tela de escolha de contexto para usuários que possuem
 * acesso simultâneo ao painel White Label E a um tenant (de qualquer `kind`).
 *
 * Layout split-screen idêntico à tela de Login: branding à esquerda
 * (`AuthBrandingPanel`) e formulário/escolha à direita, com mesma logo,
 * tipografia, espaçamento, divisores e estilo de botões usados em `/auth`.
 *
 * CHANGELOG:
 *   v1.5.0 — 2026-05-29 — Lado direito reescrito para espelhar 100% o
 *            formulário do Login: logo grande, título h2 forte, blocos
 *            sem cards/ícones coloridos, botões h-12 rounded-xl, divisor
 *            "ou" e rodapé minimalista (email + link "Sair"). Lógica de
 *            roteamento inalterada.
 *   v1.4.0 — Layout split-screen com AuthBrandingPanel à esquerda.
 *   v1.3.0 — Redesign premium inicial com cards e blobs.
 *   v1.2.0 — Remove persistência em localStorage.
 *   v1.1.0 — Aceita tenants `wl_internal`.
 *   v1.0.0 — Implementação inicial.
 */
import { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, ArrowRight, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/useAuth';
import { useIsWlUser } from '@/hooks/useIsWlUser';
import { useSystemSettings } from '@/hooks/useSystemSettings';
import { AuthBrandingPanel } from '@/components/auth/AuthBrandingPanel';

// Canal/sinal cross-tab. Reutiliza o BroadcastChannel do logout — o type
// `CONTEXT_SELECTED` é distinto de `LOGOUT_STARTED`, então não há conflito.
const AUTH_CHANNEL_NAME = 'uaizy-auth';
const CONTEXT_SIGNAL_KEY = 'uaizy:context-signal';

type ContextChoice = 'wl' | 'tenant';

interface ContextSignalPayload {
  type: 'CONTEXT_SELECTED';
  choice: ContextChoice;
  isAgent: boolean;
  at: number;
}

export default function SelectContext() {
  const navigate = useNavigate();
  const { user, isAgent, loading, dataLoaded, isTenantMember, currentTenant, availableTenants, signOut } = useAuth();
  const { isWlUser, isResolved: wlResolved } = useIsWlUser();
  const { branding } = useSystemSettings();

  // Resolve o destino final a partir da escolha do usuário.
  const resolveDestination = useCallback(
    (choice: ContextChoice, agentFlag: boolean): string => {
      if (choice === 'wl') return '/wl';
      return agentFlag ? '/tenant/chat' : '/tenant';
    },
    [],
  );

  // Redirecionamentos defensivos: usuário sem cruzamento volta para o roteador.
  useEffect(() => {
    if (loading || !dataLoaded || !wlResolved) return;
    if (!user) {
      navigate('/auth', { replace: true });
      return;
    }
    if (!isWlUser || !isTenantMember) {
      navigate('/', { replace: true });
    }
  }, [user, loading, dataLoaded, wlResolved, isWlUser, isTenantMember, navigate]);

  // ---------------------------------------------------------------------------
  // Sincronização cross-tab da escolha de contexto.
  // Quando o usuário escolhe um painel em uma aba, todas as outras abas em
  // /select-context devem sair juntas — replicando o comportamento padrão
  // do fluxo de usuário comum.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    let channel: BroadcastChannel | null = null;

    const handleRemoteChoice = (payload: ContextSignalPayload) => {
      if (!payload || payload.type !== 'CONTEXT_SELECTED') return;
      navigate(resolveDestination(payload.choice, payload.isAgent), { replace: true });
    };

    try {
      if (typeof BroadcastChannel !== 'undefined') {
        channel = new BroadcastChannel(AUTH_CHANNEL_NAME);
        channel.onmessage = (ev) => {
          if (ev?.data?.type === 'CONTEXT_SELECTED') {
            handleRemoteChoice(ev.data as ContextSignalPayload);
          }
        };
      }
    } catch {
      /* noop — fallback storage event abaixo */
    }

    const onStorage = (ev: StorageEvent) => {
      if (ev.key !== CONTEXT_SIGNAL_KEY || !ev.newValue) return;
      try {
        handleRemoteChoice(JSON.parse(ev.newValue) as ContextSignalPayload);
      } catch {
        /* payload inválido — ignora */
      }
    };
    window.addEventListener('storage', onStorage);

    return () => {
      window.removeEventListener('storage', onStorage);
      try { channel?.close(); } catch { /* noop */ }
    };
  }, [navigate, resolveDestination]);

  if (loading || !dataLoaded || !wlResolved) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Prioridade: tenant `customer` primeiro; se só houver `wl_internal`, usa ele.
  const tenantForCard =
    availableTenants.find((t) => t.kind !== 'wl_internal') ??
    availableTenants[0] ??
    currentTenant;
  const isInternalOnly = tenantForCard?.kind === 'wl_internal';

  const goTo = (choice: ContextChoice) => {
    // 1. Publicar sinal cross-tab ANTES de navegar localmente.
    const payload: ContextSignalPayload = {
      type: 'CONTEXT_SELECTED',
      choice,
      isAgent,
      at: Date.now(),
    };

    try {
      if (typeof BroadcastChannel !== 'undefined') {
        const ch = new BroadcastChannel(AUTH_CHANNEL_NAME);
        ch.postMessage(payload);
        setTimeout(() => { try { ch.close(); } catch { /* noop */ } }, 50);
      }
    } catch {
      /* noop */
    }

    // Fallback: storage event para abas sem BroadcastChannel.
    try {
      localStorage.setItem(CONTEXT_SIGNAL_KEY, JSON.stringify(payload));
      setTimeout(() => {
        try { localStorage.removeItem(CONTEXT_SIGNAL_KEY); } catch { /* noop */ }
      }, 500);
    } catch {
      /* noop */
    }

    // 2. Navegar localmente.
    navigate(resolveDestination(choice, isAgent), { replace: true });
  };

  return (
    <div className="min-h-screen flex bg-background">
      {/* ========== LADO ESQUERDO: branding (idêntico ao /auth) ========== */}
      <AuthBrandingPanel />

      {/* ========== LADO DIREITO: escolha de contexto (estilo Login) ========== */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-8 lg:p-12 relative">
        {/* Background sutil para mobile (igual ao Auth) */}
        <div className="lg:hidden absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent pointer-events-none" />

        <div className="w-full max-w-md relative animate-auth-fade-in">
          {/* Logo centralizada — mesmo bloco do Auth.tsx */}
          <div className="flex justify-center mb-8">
            {(branding.login_logo_url || branding.logo_url) ? (
              <img
                src={branding.login_logo_url || branding.logo_url!}
                alt={branding.app_name}
                className="h-auto max-h-[180px] max-w-[380px] object-contain"
              />
            ) : (
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-primary/10">
                  {branding.icon_url ? (
                    <img src={branding.icon_url} alt="" className="h-8 w-8 object-contain" />
                  ) : (
                    <MessageSquare className="h-8 w-8 text-primary" />
                  )}
                </div>
                <span className="text-2xl font-bold text-foreground">{branding.app_name}</span>
              </div>
            )}
          </div>

          {/* Cabeçalho — tipografia idêntica ao Login */}
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">
              Escolha onde deseja entrar
            </h2>
            <p className="text-muted-foreground">
              Sua conta tem acesso a mais de um contexto. Selecione o painel para continuar.
            </p>
          </div>

          {/* Bloco 1: Painel White Label */}
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-[11px] font-semibold tracking-[0.18em] uppercase text-muted-foreground">
                Administração
              </Label>
              <p className="text-sm text-muted-foreground leading-relaxed">
                <span className="font-medium text-foreground">Painel White Label</span> — plataforma,
                tenants, usuários, provedores e configurações globais.
              </p>
            </div>
            <Button
              onClick={() => goTo('wl')}
              className="w-full h-12 rounded-xl group/btn"
              disabled={loading}
            >
              Entrar no painel WL
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover/btn:translate-x-1" />
            </Button>
          </div>

          {/* Divisor "ou" — cópia literal do separador do Auth */}
          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border/50" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-background px-4 text-sm text-muted-foreground">ou</span>
            </div>
          </div>

          {/* Bloco 2: Painel do Tenant */}
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-[11px] font-semibold tracking-[0.18em] uppercase text-muted-foreground">
                Operação
              </Label>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium text-foreground">
                  {tenantForCard?.name ?? 'Painel do Tenant'}
                </span>
                {isInternalOnly && (
                  <Badge variant="secondary" className="text-[9px] uppercase tracking-wider px-1.5 py-0">
                    Interno
                  </Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Operação da empresa: CRM, atendimento, financeiro, tarefas e mais.
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() => goTo('tenant')}
              className="w-full h-12 rounded-xl border-border/50 hover:bg-accent transition-all duration-300 group/btn"
            >
              Entrar em {tenantForCard?.name ?? 'tenant'}
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover/btn:translate-x-1" />
            </Button>
          </div>

          {/* Rodapé minimalista — estilo do link "Esqueceu a senha?" do Auth */}
          <div className="text-center mt-8 text-sm text-muted-foreground">
            Conectado como{' '}
            <span className="font-medium text-foreground">{user?.email}</span>
            <span className="mx-2 text-border">·</span>
            <button
              type="button"
              onClick={() => signOut()}
              className="text-primary hover:underline font-medium"
            >
              Sair
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
